import {
  Avatar,
  Badge,
  Card,
  CardHeader,
  EmptyState,
  Input,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from "@kontaktfix/ui";
import { ArrowLeftIcon, MagnifyingGlassIcon, UsersIcon } from "@phosphor-icons/react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CarddavCard } from "@/components/carddav-card";
import { ListBadge } from "@/components/list-badge";
import { Page, PageHeader } from "@/components/page";
import { RecipientStatusLabel } from "@/components/recipient-status";
import { formatRelative } from "@/lib/i18n";
import { useLists, useMe, usePeople, usePerson } from "@/lib/queries";

export function PeoplePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: people, isLoading } = usePeople();
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return (people ?? []).filter(
      (p) => !q || p.displayName.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q),
    );
  }, [people, query]);

  return (
    <Page>
      <PageHeader title={t("people.title")} description={t("people.description")} />
      <Input
        icon={<MagnifyingGlassIcon />}
        className="mb-3 max-w-sm"
        placeholder={t("people.search")}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <Card>
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-10" />
            <Skeleton className="h-10" />
          </div>
        ) : !people?.length ? (
          <EmptyState icon={<UsersIcon />} title={t("people.empty")} description={t("people.emptyText")} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t("people.columns.person")}</Th>
                <Th>{t("people.columns.status")}</Th>
                <Th className="hidden text-right sm:table-cell">{t("people.columns.lists")}</Th>
                <Th className="hidden text-right sm:table-cell">{t("people.columns.contacts")}</Th>
                <Th className="hidden text-right md:table-cell">{t("people.columns.lastSync")}</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <Tr
                  key={p.userId}
                  onClick={() => navigate({ to: "/people/$userId", params: { userId: p.userId } })}
                >
                  <Td>
                    <div className="flex items-center gap-3">
                      <Avatar name={p.displayName} size={28} />
                      <div className="min-w-0">
                        <div className="truncate font-medium">{p.displayName}</div>
                        <div className="truncate text-[12px] text-fg-muted">{p.email}</div>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <RecipientStatusLabel recipient={p} />
                  </Td>
                  <Td className="hidden text-right tabular-nums sm:table-cell">{p.listCount}</Td>
                  <Td className="hidden text-right tabular-nums sm:table-cell">{p.contactCount}</Td>
                  <Td className="hidden text-right text-[12.5px] text-fg-subtle md:table-cell">
                    {p.lastSyncedAt ? formatRelative(p.lastSyncedAt) : t("common.never")}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Page>
  );
}

export function PersonPage() {
  const { t } = useTranslation();
  const { userId } = useParams({ from: "/app/people/$userId" });
  const { data: person, isLoading } = usePerson(userId);
  const { data: lists = [] } = useLists();
  const { data: me } = useMe();

  if (isLoading || !person) {
    return (
      <Page>
        <Skeleton className="mb-4 h-10 w-64" />
        <Skeleton className="h-40" />
      </Page>
    );
  }

  const byList = new Map<string, typeof person.sources>();
  for (const s of person.sources) byList.set(s.listId, [...(byList.get(s.listId) ?? []), s]);

  return (
    <Page>
      <Link
        to="/people"
        className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
      >
        <ArrowLeftIcon className="size-3.5" />
        {t("people.title")}
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Avatar name={person.displayName} size={36} />
            {person.displayName}
          </span>
        }
        description={person.email}
        actions={<RecipientStatusLabel recipient={person} />}
      />
      <div className="grid gap-6">
        <Card>
          <CardHeader
            title={t("people.receives")}
            description={`${person.contactCount} ${t("people.columns.contacts")} · ${person.lastSyncedAt ? formatRelative(person.lastSyncedAt) : t("common.never")}`}
          />
          <div className="divide-y divide-border">
            {[...byList.entries()].map(([listId, sources]) => {
              const list = lists.find((l) => l.id === listId);
              return (
                <div
                  key={listId}
                  className="flex flex-col gap-1.5 px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  {list ? (
                    <Link to="/lists/$listId" params={{ listId }} className="hover:underline">
                      <ListBadge name={list.name} color={list.color} />
                    </Link>
                  ) : (
                    <span />
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {sources.map((s) => (
                      <Badge
                        key={s.via.type === "group" ? s.via.groupId : "direct"}
                        tone={s.via.type === "group" ? "neutral" : "accent"}
                      >
                        {s.via.type === "group"
                          ? t("people.viaGroup", { group: s.via.groupName })
                          : t("people.direct")}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            })}
            {!byList.size && <p className="px-5 py-4 text-[13px] text-fg-muted">{t("common.none")}</p>}
          </div>
        </Card>
        {me?.role === "admin" && person.sources.length > 0 && (
          <CarddavCard userId={person.userId} name={person.displayName} />
        )}
      </div>
    </Page>
  );
}
